import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import RestaurantDetail from './pages/RestaurantDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import MyReservations from './pages/MyReservations';
import MyTickets from './pages/MyTickets';
import MyProfile from './pages/MyProfile';
import Membership from './pages/Membership';
import MembershipPayments from './pages/MembershipPayments';
import VerifyMembership from './pages/VerifyMembership';
import MembershipPaymentResult from './pages/MembershipPaymentResult';
import MembershipPay from './pages/MembershipPay';
import ForgotPassword from './pages/ForgotPassword';
import VerifyResetOtp from './pages/VerifyResetOtp';
import ResetPassword from './pages/ResetPassword';
import RateReservation from './pages/RateReservation';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import GuestPayment from './pages/GuestPayment';
import GuestPaymentSuccess from './pages/GuestPaymentSuccess';
import ReservationWidget from './pages/ReservationWidget';
import ReservationWidget2 from './pages/ReservationWidget2';
import ExperiencePage from './pages/ExperiencePage';
import ProgramPage from './pages/ProgramPage';
import MembershipWidget from './pages/MembershipWidget';
import EventTicketPayment from './pages/EventTicketPayment';
import GuestPortal from './pages/Portal/GuestPortal';
import OrgLayout from './pages/OrgLayout';
import PoweredBy from './components/PoweredBy';
import { setPageSeo } from './utils/seo';

// SEO tags for the slug-less /portal landing.
function PortalLandingSeo() {
  useEffect(() => {
    setPageSeo({ title: 'Guest portal | ez restaurants', canonicalPath: '/portal' });
  }, []);
  return null;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes (no org context) */}
        <Route path="/" element={<Home />} />
        <Route path="/restaurant/:id" element={<RestaurantDetail />} />
        {/* /widget/:slug is the shareable link (same restaurant slugs as the
            guest portal). The bare /widget?restaurantId=N form stays mounted
            because restaurants have already embedded it on their own sites. */}
        <Route path="/widget" element={<ReservationWidget />} />
        <Route path="/widget/:slug" element={<ReservationWidget />} />
        <Route path="/widget2" element={<ReservationWidget2 />} />
        <Route path="/widget2/:slug" element={<ReservationWidget2 />} />
        {/* Canonical experience URL. /experience/:id is the legacy shape kept
            alive for links already sent out in emails, tickets and QR codes —
            ExperiencePage redirects it to /ex/:slug once it has loaded. */}
        <Route path="/ex/:slug" element={<ExperiencePage />} />
        <Route path="/experience/:id" element={<ExperiencePage />} />
        <Route path="/program/:restaurantId" element={<ProgramPage />} />
        <Route path="/membership-widget" element={<MembershipWidget />} />
        <Route path="/verify-membership/:id" element={<VerifyMembership />} />
        <Route path="/rate/:reservationId" element={<RateReservation />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />

        {/* Fallback membership result routes (no org in URL). Hit when the
            Paymob callback couldn't resolve the org — a guest who paid
            should never land on the platform home. Same result page,
            just without the org-scoped nav/CTAs. */}
        <Route path="/membership/payment-success" element={<MembershipPaymentResult />} />
        <Route path="/membership/payment-refunded" element={<MembershipPaymentResult />} />
        <Route path="/membership/payment-error" element={<MembershipPaymentResult />} />
        <Route path="/membership/pay/:purchaseId" element={<MembershipPay />} />
        <Route path="/pay/:token" element={<GuestPayment />} />
        <Route path="/guest-payment-success" element={<GuestPaymentSuccess />} />
        <Route path="/event-payment/:token" element={<EventTicketPayment />} />
        <Route path="/portal/r/:slug" element={<GuestPortal />} />
        <Route
          path="/portal"
          element={
            <div style={{ padding: 40, fontFamily: "Arial, sans-serif", textAlign: "center", maxWidth: 560, margin: "60px auto" }}>
              <PortalLandingSeo />
              <h2 style={{ color: "#4A009F" }}>Open the link your restaurant sent you</h2>
              <p style={{ color: "#555", marginTop: 12 }}>
                The guest portal is now per-restaurant. Please use the link from your confirmation email, or contact the venue.
              </p>
              <PoweredBy />
            </div>
          }
        />

        {/* Organization-scoped routes: /o/:orgSlug/* */}
        <Route path="/o/:orgSlug" element={<OrgLayout />}>
          <Route index element={<Navigate to="login" replace />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="verify-reset-otp" element={<VerifyResetOtp />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="my-reservations" element={<MyReservations />} />
          <Route path="my-tickets" element={<MyTickets />} />
          <Route path="my-profile" element={<MyProfile />} />
          <Route path="membership" element={<Membership />} />
          <Route path="membership/payments" element={<MembershipPayments />} />
          <Route path="membership/payment-success" element={<MembershipPaymentResult />} />
          <Route path="membership/payment-refunded" element={<MembershipPaymentResult />} />
          <Route path="membership/payment-error" element={<MembershipPaymentResult />} />
          <Route path="membership/pay/:purchaseId" element={<MembershipPay />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
